/**
 * <SubscriptionNextStepCard> — la carte « Et ensuite ? » (organism, L0.1).
 *
 * Les trois états de `renderActivationSuiteCard` (`ParentSubscriptionPortalScreen.js:1430-1553`) :
 * la suite à activer, la reprise déjà planifiée, et l'échéance dont l'offre n'est pas encore
 * calculable. Un profil non éligible n'a **aucune** carte — le view-model passe `null`, il n'y a
 * pas de bloc neutre « aucune reprise planifiée ».
 *
 * Le prix n'est formulé qu'une fois (`priceLine`), comme le legacy : la carte annonçait autrefois
 * le mensuel, puis le 1ᵉʳ mois, puis un rappel. Le trou de couverture est un bloc teinté à part —
 * c'est la seule CONTRAINTE de la carte, et deux boîtes colonnes côte à côte ne s'y disputent pas.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Card } from "../../molecules/Card"
import { HorizontalList } from "../../atoms/HorizontalList"
import { ObjectiveHoursChip } from "../../molecules/ObjectiveHoursChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SubscriptionLine } from "../../molecules/SubscriptionLine"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { type UISubscriptionNextStep } from "../../../ports/primary/ui/types"

export type SubscriptionNextStepCardProps = {
    nextStep: UISubscriptionNextStep
    /** « Activer la suite » — état `activate`. */
    onActivate?: () => void
    /** « Modifier » — états `activate` et `planned` (si `editable`). */
    onEdit?: () => void
    /**
     * Libellé du geste de modification quand il est SEUL et pleine largeur (état `planned`) —
     * « Modifier » par défaut, ce que dit le portail ; l'arrivée du lien signé écrit « Modifier la
     * reprise ». L'état `activate`, lui, garde « Modifier » quoi qu'on passe : ce bouton-là vit
     * dans un tiers de rangée, où un libellé de deux mots déborde de sa boîte à 390 px.
     */
    editLabel?: string
    /** « Prolonger … » du bloc trou de couverture. */
    onExtend?: () => void
    extendLoading?: boolean
    /** « Contacter le support » — état `uncomputable`. */
    onSupport?: () => void
    testID?: string
}

export function SubscriptionNextStepCard(props: SubscriptionNextStepCardProps) {
    const {
        nextStep,
        onActivate,
        onEdit,
        editLabel = "Modifier",
        onExtend,
        extendLoading = false,
        onSupport,
        testID,
    } = props
    const {
        variant,
        pillLabel,
        pillIcon,
        headline,
        offerLine,
        priceLine,
        priceDiscounted = false,
        detailLines = [],
        objectives = [],
        guarantees = [],
        coverageGap,
        message,
        editable = true,
    } = nextStep

    // Le choix est fait → registre apaisé (vert). Tant qu'il reste à prendre, la carte porte
    // une date limite : elle se lit en jaune signature, pas en bleu d'information.
    const settled = variant === "planned"

    return (
        <Card p="4" leftBorderColor={settled ? "success" : "accent-strong"} testID={testID}>
            <Stack gap="3">
                <Row>
                    <Badge text={pillLabel} iconName={pillIcon} variant={settled ? "success" : "primary"} tinted />
                </Row>

                {(headline != null || offerLine != null) && (
                    <Stack gap="1">
                        {headline != null && <Title size="text-xl">{headline}</Title>}
                        {offerLine != null && (
                            <Text size="text-md" color="text-muted">
                                {offerLine}
                            </Text>
                        )}
                    </Stack>
                )}

                {objectives.length > 0 && (
                    <HorizontalList gap="2">
                        {objectives.map((objective) => (
                            <ObjectiveHoursChip
                                key={objective.id}
                                subjects={objective.subjects}
                                hours={objective.hours}
                            />
                        ))}
                    </HorizontalList>
                )}

                {(priceLine != null || detailLines.length > 0 || message != null) && (
                    <Stack gap="1">
                        {priceLine != null && (
                            <Text size="text-xl" weight="extrabold" color={priceDiscounted ? "success" : "primary"}>
                                {priceLine}
                            </Text>
                        )}
                        {detailLines.map((line) => (
                            <Text key={line} size="text-sm" color="text-muted">
                                {line}
                            </Text>
                        ))}
                        {message != null && (
                            <Text size="text-sm" color="text-muted">
                                {message}
                            </Text>
                        )}
                    </Stack>
                )}

                {guarantees.length > 0 && (
                    <Stack gap="2">
                        {guarantees.map((line) => (
                            <SubscriptionLine key={line.icon} line={line} />
                        ))}
                    </Stack>
                )}

                {coverageGap != null && (
                    <Card background="surface-subtle" leftBorderColor="info" p="3">
                        <Stack gap="3">
                            <Banner icon="calendar-times" color="info" message={coverageGap.text} align="start" />
                            {coverageGap.extendLabel != null && onExtend != null && (
                                <Button
                                    label={coverageGap.extendLabel}
                                    iconName="plus-circle"
                                    color="primary"
                                    fullWidth
                                    loading={extendLoading}
                                    disabled={extendLoading}
                                    onPress={onExtend}
                                />
                            )}
                        </Stack>
                    </Card>
                )}

                <Actions
                    variant={variant}
                    editable={editable}
                    onActivate={onActivate}
                    onEdit={onEdit}
                    editLabel={editLabel}
                    onSupport={onSupport}
                />
            </Stack>
        </Card>
    )
}

type ActionsProps = Pick<SubscriptionNextStepCardProps, "onActivate" | "onEdit" | "onSupport"> & {
    variant: UISubscriptionNextStep["variant"]
    editable: boolean
    editLabel: string
}

/** Les gestes de chaque état — « Activer la suite » porte l'icône pour la paire. */
function Actions(props: ActionsProps) {
    const { variant, editable, onActivate, onEdit, editLabel, onSupport } = props

    if (variant === "uncomputable") {
        if (onSupport == null) return null
        return (
            <Button label="Contacter le support" iconName="headset" variant="outline" fullWidth onPress={onSupport} />
        )
    }

    if (variant === "planned") {
        if (!editable || onEdit == null) return null
        return <Button label={editLabel} variant="outline" fullWidth onPress={onEdit} />
    }

    if (onActivate == null && onEdit == null) return null

    // « MODIFIER » est insécable : il ne peut pas se replier, seulement récupérer de la place.
    // D'où les deux tiers / un tiers, l'icône sur la seule action primaire — et le libellé COURT,
    // qu'un appelant ne peut pas rallonger : à 390 px, « Modifier la reprise » sortait de sa boîte.
    return (
        <Row gap="2" align="stretch">
            {onActivate != null && (
                <Stack style={styles.primaryAction}>
                    <Button label="Activer la suite" iconName="bolt" fullWidth onPress={onActivate} />
                </Stack>
            )}
            {onEdit != null && (
                <Stack style={styles.secondaryAction}>
                    <Button label="Modifier" variant="outline" fullWidth onPress={onEdit} />
                </Stack>
            )}
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    primaryAction: { flex: 2 },
    secondaryAction: { flex: 1 },
}))
